import React, { useMemo } from "react";

import { compact, isEmpty } from "lodash/fp";
import { useParams } from "react-router-dom";

import useMeetingReport from "@app/analytics/MeetingReport/hooks/useMeetingReport";
import useMeetingReportSessionNotes from "@app/analytics/MeetingReport/hooks/useMeetingReportSessionNotes";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import useVideoRecording from "@app/analytics/MeetingReport/hooks/useVideoRecording";
import {
  generateTextFileFromTranscript,
  generateTextFileFromSummary,
} from "@app/analytics/MeetingReport/utils/transcriptUtils";
import * as Events from "@app/analytics/tracking/events";
import useApps from "@app/common/hooks/useApps";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import ROUTES from "@app/common/routes";
import { withQueryParamsUrl } from "@app/common/utils";
import Config from "@app/config";

import DownloadButtonAndPopover from "./DownloadButtonAndPopover";
import DownloadMenuDialog from "./DownloadMenuDialog";
import type { DownloadConfig } from "./types";
import * as DownloadUtils from "./utils";

const DownloadMenu = ({
  disabled = false,
  variant,
}: {
  disabled?: boolean;
  variant: "dialog" | "menu";
}) => {
  const { client } = useApps();
  const { sessionId } = useParams();
  const { canAccessTranscript, canAccessPlayback } = useFeatureAccess();

  const {
    data: {
      highlightReelUrl,
      isRestrictedReport,
      isNotetakerReport,
      title,
      startTime,
    },
  } = useSessionMetadata({ enabled: !disabled });

  const cannotAccessArtifacts =
    isRestrictedReport || (!canAccessTranscript && !canAccessPlayback);
  const queriesEnabled = !isRestrictedReport && !cannotAccessArtifacts;

  const { meetingStartTime } = useMeetingReport({
    enabled: queriesEnabled,
  });

  const { videoRecording, hasVideoRecording, trailerUrl } = useVideoRecording({
    enabled: queriesEnabled && canAccessPlayback && !isNotetakerReport,
  });

  const {
    rawTurns,
    summary,
    isEmpty: transcriptAndSummaryIsEmpty,
  } = useTranscript({ enabled: queriesEnabled });
  const { notes } = useMeetingReportSessionNotes({ enabled: queriesEnabled });

  const transcriptTxtFile = useMemo(
    () =>
      generateTextFileFromTranscript(rawTurns, {
        title,
        startTime: meetingStartTime ?? startTime,
      }),
    [rawTurns, title, meetingStartTime?.toISO()]
  );

  const summaryTxtFile = useMemo(
    () =>
      generateTextFileFromSummary(summary, notes, {
        title,
        startTime: meetingStartTime ?? startTime,
        isNotetakerReport,
      }),
    [summary, title, notes, meetingStartTime?.toISO(), isNotetakerReport]
  );

  const canDownloadTranscript =
    canAccessTranscript && !transcriptAndSummaryIsEmpty && !isNotetakerReport;
  const canDownloadSummary =
    canAccessTranscript && (!transcriptAndSummaryIsEmpty || !isEmpty(notes));
  const canDownloadTrailer = canAccessPlayback && trailerUrl != null;
  const canDownloadHighlightReel =
    canAccessPlayback && highlightReelUrl != null;
  const canDownloadVideo = canAccessPlayback && hasVideoRecording;

  const cannotDownloadAnything = isEmpty(
    compact([
      canDownloadTranscript,
      canDownloadSummary,
      canDownloadTrailer,
      canDownloadHighlightReel,
      canDownloadVideo,
    ])
  );

  if (cannotDownloadAnything) return null;

  const downloads: DownloadConfig = {
    summary: {
      ...DownloadUtils.getDownloadOptsForClient({
        client,
        externalDownloadHref: withQueryParamsUrl(ROUTES.GO.DOWNLOAD_SUMMARY, {
          sessionId,
        }),
        data: summaryTxtFile,
        fileName: DownloadUtils.getDownloadTitle(
          title,
          isNotetakerReport ? "Notes" : "Summary"
        ),
      }),
      label: isNotetakerReport
        ? "Meeting notes (.txt)"
        : "Meeting summary (.txt)",
      onClick: Events.downloadSummary,
      show: canDownloadSummary,
    },
    transcript: {
      ...DownloadUtils.getDownloadOptsForClient({
        client,
        externalDownloadHref: withQueryParamsUrl(
          ROUTES.GO.DOWNLOAD_TRANSCRIPT,
          { sessionId }
        ),
        data: transcriptTxtFile,
        fileName: DownloadUtils.getDownloadTitle(title, "Transcript"),
      }),
      label: "Meeting transcript (.txt)",
      onClick: Events.downloadTranscript,
      show: canDownloadTranscript,
    },
    trailer: {
      label: "Meeting trailer (.mp4)",
      href: DownloadUtils.getVideoDownloadUrl(
        trailerUrl ?? "",
        DownloadUtils.getDownloadTitle(title, "Trailer")
      ),
      download: DownloadUtils.getDownloadTitle(title, "Trailer"),
      onClick: Events.downloadTrailer,
      show: canDownloadTrailer,
    },
    highlightReel: {
      label: "Meeting highlights (.mp4)",
      href: DownloadUtils.getVideoDownloadUrl(
        highlightReelUrl ?? "",
        DownloadUtils.getDownloadTitle(title, "Highlights")
      ),
      download: DownloadUtils.getDownloadTitle(title, "Highlights"),
      onClick: Events.downloadHighlights,
      show: canDownloadHighlightReel,
    },
    videoRecording: {
      label: "Meeting video (.mp4)",
      href: DownloadUtils.getVideoRecordingDownloadUrl(videoRecording, {
        filename: DownloadUtils.getDownloadTitle(title, "Recording"),
        recordingsUrl: Config.RECORDINGS_URL,
      }),
      download: DownloadUtils.getDownloadTitle(title, "Recording"),
      onClick: Events.downloadRecording,
      show: canDownloadVideo,
    },
  };

  if (variant === "menu") {
    return (
      <DownloadButtonAndPopover disabled={disabled} downloads={downloads} />
    );
  }

  return <DownloadMenuDialog disabled={disabled} downloads={downloads} />;
};

export default DownloadMenu;
